import {
  internetConnectionTypeOrder,
  type InternetConnectionType,
  type InternetPlan,
} from "@/lib/fetch/internet";

/** Upload at least this share of download counts as a symmetric line. */
const SYMMETRIC_RATIO = 0.9;

export const isSymmetricPlan = (plan: InternetPlan) =>
  plan.downloadMbps !== null &&
  plan.uploadMbps !== null &&
  plan.downloadMbps > 0 &&
  plan.uploadMbps >= plan.downloadMbps * SYMMETRIC_RATIO;

export const isMonthlyPlan = (plan: InternetPlan) =>
  plan.minimumContractMonths !== null && plan.minimumContractMonths <= 1;

/**
 * The recurring monthly bill after any introduction: the regular service
 * price plus monthly equipment rental and any separately billed network fee.
 * Null when the provider only quotes the price after an address check.
 */
export const getRunningMonthlyCents = (plan: InternetPlan) =>
  plan.regularMonthlyPriceCents === null
    ? null
    : plan.regularMonthlyPriceCents +
      (plan.equipmentMonthlyFeeCents ?? 0) +
      (plan.networkMonthlyFeeCents ?? 0);

/** Known one-off costs, and whether any one-off item is undisclosed. */
export const getOneOffCosts = (plan: InternetPlan) => {
  const items = [
    plan.activationFeeCents,
    plan.installationFeeCents,
    plan.equipmentUpfrontFeeCents,
  ];
  return {
    totalCents: items.reduce<number>((sum, value) => sum + (value ?? 0), 0),
    activationKnown: plan.activationFeeCents !== null,
  };
};

export type InternetPlanCost = {
  /** Months you pay for: your stay, or the minimum term when it is longer. */
  billedMonths: number;
  /** Months billed at the introductory price. */
  introMonths: number;
  oneOffCents: number;
  totalCents: number;
  /** Total spread over the months you actually stay. */
  effectiveMonthlyCents: number;
  /** True when the minimum term outlasts the stay. */
  termOutlastsStay: boolean;
  activationKnown: boolean;
};

/**
 * Total cost of keeping a plan for `stayMonths`, honouring the minimum term
 * (an early exit is billed as the remaining months) and any introductory
 * period. Null when the plan has no published monthly price.
 */
export const getPlanCost = (
  plan: InternetPlan,
  stayMonths: number,
): InternetPlanCost | null => {
  const running = getRunningMonthlyCents(plan);
  if (running === null) return null;

  const billedMonths = Math.max(stayMonths, plan.minimumContractMonths ?? 0);
  const introMonths =
    plan.introMonthlyPriceCents !== null && plan.introDurationMonths
      ? Math.min(plan.introDurationMonths, billedMonths)
      : 0;
  const introSaving =
    introMonths > 0 && plan.regularMonthlyPriceCents !== null
      ? (plan.regularMonthlyPriceCents - (plan.introMonthlyPriceCents ?? 0)) *
        introMonths
      : 0;
  const oneOff = getOneOffCosts(plan);
  const totalCents = running * billedMonths - introSaving + oneOff.totalCents;

  return {
    billedMonths,
    introMonths,
    oneOffCents: oneOff.totalCents,
    totalCents,
    effectiveMonthlyCents: Math.round(totalCents / Math.max(stayMonths, 1)),
    termOutlastsStay: billedMonths > stayMonths,
    activationKnown: oneOff.activationKnown,
  };
};

/** Monthly euros per 100 Mbps of download: lower is better value. */
export const getValueCentsPer100Mbps = (plan: InternetPlan) => {
  const running = getRunningMonthlyCents(plan);
  if (running === null || !plan.downloadMbps) return null;
  return Math.round(running / (plan.downloadMbps / 100));
};

export type InternetPlanSummary = {
  planCount: number;
  connectionTypes: InternetConnectionType[];
  networkCount: number;
  networks: { slug: string; name: string }[];
  providerCount: number;
  providers: { slug: string; name: string }[];
  fromPriceCents: number | null;
  maxDownloadMbps: number | null;
  maxUploadMbps: number | null;
  shortestTermMonths: number | null;
  monthlyCount: number;
  symmetricCount: number;
  ownRouterCount: number;
  staticIpCount: number;
};

const uniqueBy = <T, K>(items: T[], key: (item: T) => K) =>
  Array.from(new Map(items.map((item) => [key(item), item])).values());

/** Range facts for any set of plans: a provider, a network or a tier. */
export const summarizeInternetPlans = (
  plans: InternetPlan[],
): InternetPlanSummary => {
  const present = new Set(plans.map((plan) => plan.connectionType));
  const prices = plans
    .map(getRunningMonthlyCents)
    .filter((value): value is number => value !== null);
  const downloads = plans
    .map((plan) => plan.downloadMbps)
    .filter((value): value is number => value !== null);
  const uploads = plans
    .map((plan) => plan.uploadMbps)
    .filter((value): value is number => value !== null);
  const terms = plans
    .map((plan) => plan.minimumContractMonths)
    .filter((value): value is number => value !== null);
  const networks = uniqueBy(
    plans.flatMap((plan) => (plan.network ? [plan.network] : [])),
    (network) => network.slug,
  ).sort((a, b) => a.name.localeCompare(b.name));
  const providers = uniqueBy(
    plans.map((plan) => plan.provider),
    (provider) => provider.slug,
  ).sort((a, b) => a.name.localeCompare(b.name));

  return {
    planCount: plans.length,
    connectionTypes: internetConnectionTypeOrder.filter((type) =>
      present.has(type),
    ),
    networkCount: networks.length,
    networks,
    providerCount: providers.length,
    providers,
    fromPriceCents: prices.length ? Math.min(...prices) : null,
    maxDownloadMbps: downloads.length ? Math.max(...downloads) : null,
    maxUploadMbps: uploads.length ? Math.max(...uploads) : null,
    shortestTermMonths: terms.length ? Math.min(...terms) : null,
    monthlyCount: plans.filter(isMonthlyPlan).length,
    symmetricCount: plans.filter(isSymmetricPlan).length,
    ownRouterCount: plans.filter((plan) => plan.ownRouterSupported === true)
      .length,
    staticIpCount: plans.filter((plan) => plan.staticIp === true).length,
  };
};

/** Download bands for the price ladder; each links to its speed filter. */
export const INTERNET_SPEED_BANDS = [
  { key: "basic", min: 0, max: 150, filter: "ANY" },
  { key: "standard", min: 150, max: 450, filter: "100" },
  { key: "fast", min: 450, max: 750, filter: "500" },
  { key: "gigabit", min: 750, max: 1500, filter: "1000" },
  { key: "multi", min: 1500, max: Number.POSITIVE_INFINITY, filter: "2000" },
] as const;

export const getMedianCents = (values: number[]) => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : Math.round((sorted[middle - 1] + sorted[middle]) / 2);
};

/**
 * Plans, providers, lowest and median running price per download band.
 * Bands without plans are dropped.
 */
export const summarizeInternetSpeedBands = (plans: InternetPlan[]) =>
  INTERNET_SPEED_BANDS.map((band) => {
    const bandPlans = plans.filter(
      (plan) =>
        plan.downloadMbps !== null &&
        plan.downloadMbps >= band.min &&
        plan.downloadMbps < band.max,
    );
    const prices = bandPlans
      .map(getRunningMonthlyCents)
      .filter((value): value is number => value !== null);
    return {
      ...band,
      planCount: bandPlans.length,
      providerCount: new Set(bandPlans.map((plan) => plan.provider.slug)).size,
      fromCents: prices.length ? Math.min(...prices) : null,
      medianCents: getMedianCents(prices),
    };
  }).filter((band) => band.planCount > 0);

/** "500 Mbps", "1 Gbps", "2.5 Gbps". */
export const formatInternetSpeed = (mbps: number, locale: string) => {
  if (mbps >= 1000) {
    const gbps = mbps / 1000;
    return `${new Intl.NumberFormat(locale, {
      maximumFractionDigits: gbps % 1 === 0 ? 0 : 1,
    }).format(gbps)} Gbps`;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(
    mbps,
  )} Mbps`;
};

/** Euro amount from cents; whole euros render without decimals. */
export const formatInternetPrice = (
  cents: number,
  locale: string,
  options: { forceDecimals?: boolean } = {},
) => {
  const whole = cents % 100 === 0 && !options.forceDecimals;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
};

/** Sorts plans for display: cheapest known price first, unknown prices last. */
export const comparePlansByPrice = (a: InternetPlan, b: InternetPlan) => {
  const priceA = getRunningMonthlyCents(a);
  const priceB = getRunningMonthlyCents(b);
  if (priceA === null && priceB === null) {
    return (b.downloadMbps ?? 0) - (a.downloadMbps ?? 0);
  }
  if (priceA === null) return 1;
  if (priceB === null) return -1;
  return priceA - priceB || (b.downloadMbps ?? 0) - (a.downloadMbps ?? 0);
};

export const comparePlansBySpeed = (a: InternetPlan, b: InternetPlan) =>
  (b.downloadMbps ?? -1) - (a.downloadMbps ?? -1) ||
  (b.uploadMbps ?? -1) - (a.uploadMbps ?? -1) ||
  comparePlansByPrice(a, b);

export const comparePlansByValue = (a: InternetPlan, b: InternetPlan) => {
  const valueA = getValueCentsPer100Mbps(a);
  const valueB = getValueCentsPer100Mbps(b);
  if (valueA === null && valueB === null) return comparePlansBySpeed(a, b);
  if (valueA === null) return 1;
  if (valueB === null) return -1;
  return valueA - valueB;
};
